import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useEventSocket } from '../contexts/EventContext';  // NEU: Nutze bestehende Socket
import { useSport } from '../contexts/SportContext';
import DOMPurify from 'dompurify';
import { useToast } from '../contexts/ToastContext';
import UserProfileModal from './UserProfileModal';

// shared styles used by both EventChatModal and MessageComponent
const messageStyle = (isOwn) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isOwn ? 'flex-end' : 'flex-start',
    gap: '4px'
});

const messageBubbleStyle = (isOwn) => ({
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: '12px',
    background: isOwn ? 'linear-gradient(90deg, #00c6ff, #0066cc)' : '#f1f1f1',
    color: isOwn ? '#fff' : '#333',
    wordBreak: 'break-word'
});

const usernameStyle = {
    fontSize: '0.85rem',
    color: '#007bff',
    cursor: 'pointer',
    textDecoration: 'underline',
    userSelect: 'none'
};

// Memoized message component for performance
const MessageComponent = React.memo(({ msg, isOwn, onUsernameClick }) => {
    const sanitizedMessage = useMemo(() => 
        DOMPurify.sanitize(msg.message, {
            ALLOWED_TAGS: [], // Keine HTML Tags erlauben
            ALLOWED_ATTR: []
        }), [msg.message]);

    return (
        <div style={messageStyle(isOwn)}>
            <span
                style={usernameStyle}
                onClick={() => onUsernameClick(msg)}
            >
                {msg.nickname}
            </span>
            <div style={messageBubbleStyle(isOwn)}>
                <span dangerouslySetInnerHTML={{ __html: sanitizedMessage }} />
            </div>
            <span style={{ fontSize: '0.75rem', color: '#666' }}>
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
        </div>
    );
});

const EventChatModal = ({ event, onClose }) => {
    const { t } = useTranslation();
    const { user, api } = useAuth();  
    const socket = useEventSocket();  
    const { sportTypes } = useSport();
    // nur Teilnehmer + Ersteller dürfen chat sehen
    const isCreator = user && event.user_id === user.id;
    const myParticipation = user && event.participants ? event.participants.find(p => p.user_id === user.id) : null;
    const canAccessChat = isCreator || (myParticipation && myParticipation.status === 'accepted');

    // Basic XSS check - prevent script tags and dangerous patterns
    const dangerousPatterns = useMemo(() => [
        /<script/i,
        /javascript:/i,
        /on\w+\s*=/i,
        /<iframe/i,
        /<object/i,
        /<embed/i
    ], []);

    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [showUserProfile, setShowUserProfile] = useState(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const { showError } = useToast();
    const messagesEndRef = useRef(null);
    const localMessageIdsRef = useRef(new Set());  // Tracking für optimistic updates

    useEffect(() => {
        // wait for user info to arrive before evaluating access
        if (!user) return;
        if (!canAccessChat) {
            // instead of auto‑closing, show message inside modal
            setError('You are not allowed to view this chat.');
            return;
        }
        // we now have access – clear any previous error that was shown during race
        if (error) setError(null);

        // Load messages once; do not include `error` in deps or we'll loop
        fetchMessages();

        // join event room to receive chat updates
        if (socket) {
            socket.emit('joinRoom', `event-${event.id}`);
            // Socket Event Listener für neue Nachrichten von anderen Users
            socket.on('newChatMessage', (message) => {
                if (message.event_id === event.id && !localMessageIdsRef.current.has(message.id)) {
                    setMessages(prev => {
                        // Deduplicate: Falls Nachricht bereits existiert, nicht hinzufügen
                        if (prev.find(m => m.id === message.id)) return prev;
                        return [...prev, message];
                    });
                    localMessageIdsRef.current.delete(message.id);  // Cleanup tracking
                }
            });
        }

        return () => {
            if (socket) {
                socket.off('newChatMessage');
                socket.emit('leaveRoom', `event-${event.id}`);
            }
        };
    }, [event.id, socket, canAccessChat, user]);

    useEffect(() => {
        // Auto-scroll zu neuesten Nachrichten
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const fetchMessages = async () => {
        try {
            console.log('[Chat] Fetching messages for event:', event.id);
            const limit = 50;
            // first query to learn total count and maybe get messages
            const response = await api.get(`/api/events/${event.id}/chat?limit=${limit}&offset=0`);
            console.log('[Chat] API response:', response.data);
            let data = response.data;
            let messagesFromServer = data.messages || data;

            // if server returned pagination info and there are more than `limit` messages,
            // we need to request the *last* page, because backend sorts ASC by created_at
            if (data.pagination && data.pagination.total > limit) {
                const newOffset = Math.max(0, data.pagination.total - limit);
                console.log('[Chat] loading last page, offset', newOffset);
                const rsp2 = await api.get(
                    `/api/events/${event.id}/chat?limit=${limit}&offset=${newOffset}`
                );
                data = rsp2.data;
                messagesFromServer = data.messages || data;
            }

            setMessages(messagesFromServer);
        } catch (error) {
            console.error('[Chat] Failed to load messages:', error);
            console.error('[Chat] Error response:', error.response);
            console.error('[Chat] Error status:', error.response?.status);
            const status = error.response?.status || error.status;
            if (status === 403 || status === 401) {
                setError('You are not allowed to view this chat.');
                return;
            }
            setError('Could not load chat messages.');
        }
    };

    const handleSendMessage = useCallback(async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        // Input validation
        const trimmedMessage = newMessage.trim();
        if (trimmedMessage.length > 1000) {
            setError('Message too long. Maximum 1000 characters.');
            return;
        }

        // Basic XSS check - prevent script tags and dangerous patterns
        if (dangerousPatterns.some(pattern => pattern.test(trimmedMessage))) {
            setError('Message contains invalid content.');
            return;
        }

        if (!canAccessChat) {
            showError('You cannot send messages here.');
            return;
        }
        setSending(true);
        // Optimistic update: Füge Message lokal hinzu mit temp ID
        const tempId = `temp-${Date.now()}`;
        const optimisticMessage = {
            id: tempId,
            event_id: event.id,
            user_id: user.id,
            nickname: user.nickname,
            message: trimmedMessage, // Use validated message
            created_at: new Date().toISOString(),
            selfie_path: user.selfie_path
        };
        
        // Track diese Message um Duplicates zu vermeiden
        localMessageIdsRef.current.add(tempId);
        setMessages(prev => {
            if (!Array.isArray(prev)) return [optimisticMessage];
            return [...prev, optimisticMessage];
        });
        setNewMessage('');

        try {
            await api.post(`/api/events/${event.id}/chat`, { message: optimisticMessage.message });
            // server accepted the message; remove optimistic placeholder
            setMessages(prev => prev.filter(m => m.id !== tempId));
            localMessageIdsRef.current.delete(tempId);
            // the real message will arrive via socket and be added normally
        } catch (err) {
            // Fehler: Entferne optimistic message
            setMessages(prev => prev.filter(m => m.id !== tempId));
            localMessageIdsRef.current.delete(tempId);
            console.error('[Chat] Error sending message:', err.message || err);
            showError('Failed to send message');
        } finally {
            setSending(false);
        }
    }, [newMessage, canAccessChat, user, event.id, showError, dangerousPatterns]);

    const handleUsernameClick = useCallback((msg) => {
        setShowUserProfile(msg);
    }, []);

    const backdropStyle = {
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1500,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '20px', boxSizing: 'border-box'
    };

    const modalStyle = {
        background: '#ffffff',
        borderRadius: 'var(--border-radius-large)',
        border: '1px solid #ccc',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        maxWidth: '500px', width: '100%',
        maxHeight: '80vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden'
    };

    const headerStyle = {
        padding: '20px',
        borderBottom: '1px solid #ccc',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };

    const messagesContainerStyle = {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    };

    // messageStyle, messageBubbleStyle and usernameStyle are declared above
    // they are shared with MessageComponent so we don't redeclare them here


    const inputContainerStyle = {
        padding: '15px',
        borderTop: '1px solid #ccc',
        display: 'flex',
        gap: '10px'
    };

    const inputStyle = {
        flex: 1,
        padding: '12px 16px',
        borderRadius: 'var(--border-radius-medium)',
        border: '1px solid #ccc',
        background: '#f9f9f9',
        color: '#333',
        fontSize: '16px',
        outline: 'none'
    };

    const sendButtonStyle = {
        padding: '12px 24px',
        borderRadius: 'var(--border-radius-medium)',
        border: 'none',
        background: '#007bff',
        color: '#fff',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        userSelect: 'none'
    };

    // profileModalStyle no longer needed; using UserProfileModal component

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={backdropStyle}
        >
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
                style={modalStyle}
            >
                <div style={headerStyle}>
                    <div>
                        <h2 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                            💬 {event.title} {sportTypes[event.sport_type]?.icon} {sportTypes[event.sport_type]?.name}
                        </h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                            {t('chat')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-text-weiss)',
                            fontSize: '1.8rem',
                            cursor: 'pointer',
                            padding: '0',
                            lineHeight: '1'
                        }}
                    >
                        ✕
                    </button>
                </div>

                <div style={messagesContainerStyle}>
                    {error ? (
                    <div style={{ textAlign: 'center', color: '#d00', padding: '40px 20px', fontWeight: '600' }}>
                        {error}
                    </div>
                ) : !Array.isArray(messages) || messages.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                            {t('no_messages_yet')}
                        </div>
                    ) : (
                        messages.map((msg, index) => {
                            const isOwn = msg.user_id === user?.id;
                            return (
                                <MessageComponent 
                                    key={msg.id || index} 
                                    msg={msg} 
                                    isOwn={isOwn} 
                                    onUsernameClick={handleUsernameClick}
                                />
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} style={inputContainerStyle}>
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={t('type_message_placeholder')}
                        style={inputStyle}
                        maxLength={500}
                        autoFocus
                    />
                    <button type="submit" disabled={!newMessage.trim()} style={sendButtonStyle}>
                        ➤
                    </button>
                </form>
            </motion.div>

            <AnimatePresence>
                {showUserProfile && (
                    <UserProfileModal user={showUserProfile} onClose={() => setShowUserProfile(null)} />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default React.memo(EventChatModal);
